import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import {
  Map,
  MessageSquare,
  MessageCircle,
  Bell,
  Home,
  Menu,
  Plus,
} from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import CooperativeIcon from "./CooperativeIcon";
import { useTranslation } from "react-i18next";
import { getNotificationBadge } from "../../api/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import NewUploadsModal from "../profile/NewUploadsModal";
import PageOptionsMenu from "../profile/PageOptionsMenu";
import EditProfileModal from "../profile/EditProfileModal";
import PaymentsModal from "../profile/PaymentsModal";
import PostEditorModal from "../wall/PostEditorModal";
import BookComposerModal from "../books/BookComposerModal";
import BlogComposerModal from "../blog/BlogComposerModal";
import OfferComposerModal from "../apopilo/OfferComposerModal";
import { invalidatePersonalFeeds } from "../../hooks/useWallFeed";
import { useNotificationStore } from "../../store/notificationStore";
import NotificationsPanel from "../notifications/NotificationsPanel";
import {
  useStreamStatusStore,
  selectAnyLive,
} from "../../store/streamStatusStore";
import { firstGrapheme } from "../../emoji/graphemes";

/**
 * A stream is live somewhere in the user's street chat. Teal, the colour the
 * stream panels already use for a live slot. Top-left: the red unread dot owns
 * top-right.
 */
function LiveStreamDot({ inset, label }: { inset: number; label: string }) {
  return (
    <span
      role="img"
      aria-label={label}
      data-testid="sidenav-stream-badge"
      style={{
        position: "absolute",
        top: inset,
        left: inset,
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: "#4CB0A6",
      }}
    />
  );
}

/**
 * A nav entry: a link, or a button for the entries that open something over the page — the bell (notifications
 * panel), + (New uploads) and ☰ (page options).
 */
function NavEntry({
  item,
  label,
  className,
  open,
  onAction,
  children,
}: {
  item: NavItem;
  label: string;
  className(isActive: boolean): string;
  open: boolean;
  onAction(): void;
  children: ReactNode;
}) {
  if (item.action)
    return (
      <button
        type="button"
        title={label}
        aria-label={label}
        aria-haspopup={item.action === "options" ? "menu" : "dialog"}
        aria-expanded={open}
        data-testid={`nav-${item.key}`}
        onClick={onAction}
        className={`${className(open)} cursor-pointer`}
      >
        {children}
      </button>
    );
  return (
    <NavLink
      to={item.to!}
      title={label}
      aria-label={label}
      data-testid={`nav-${item.key}`}
      className={({ isActive }) => className(isActive)}
    >
      {children}
    </NavLink>
  );
}

type NavAction = "bell" | "uploads" | "options";
interface NavItem {
  key: string;
  to?: string;
  action?: NavAction;
  icon: ReactNode;
  label: string;
}

export default function SideNav() {
  const { t } = useTranslation();
  const username = useAuthStore((s) => s.username);
  const qc = useQueryClient();

  const initial = (firstGrapheme(username ?? "") || "?").toUpperCase();

  const { data: notiBadge } = useQuery({
    queryKey: ["notificationsBadge"],
    queryFn: getNotificationBadge,
    refetchInterval: 60_000,
    select: (badge) => (badge.notiCount ?? 0) > 0,
  });

  // The bell opens the notification list in a panel; /notifications stays for deep links.
  const [notiOpen, setNotiOpen] = useState(false);
  const [uploadsOpen, setUploadsOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingNews, setCreatingNews] = useState(false);
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [creatingBook, setCreatingBook] = useState(false);
  const [creatingBlog, setCreatingBlog] = useState(false);
  const [creatingOffer, setCreatingOffer] = useState(false);
  const pairChatBadge = useNotificationStore((s) => s.badges.pairChat);
  const streetChatBadge = useNotificationStore((s) => s.badges.streetChat);
  // A primitive boolean, so it is safe as a selector.
  const anyLive = useStreamStatusStore(selectAnyLive);
  const liveLabel = t("live_streaming_1", "LIVE streaming");

  // Order per spec §5 / Figma `Web / Sidebar / Personal` 2726:445: Cooperative (the app icon, drawn above these),
  // Home, Map, MapTxat, Chats, +, Notifications, ☰, initials. Settings and the theme switch moved under ☰ (page options).
  const navItems: NavItem[] = [
    { key: "home", to: "/feed", icon: <Home size={20} />, label: t("nav_home", "Home") },
    { key: "map", to: "/map", icon: <Map size={20} />, label: t("nav.map", "Map") },
    { key: "map-chats", to: "/map-chats", icon: <MessageSquare size={20} />, label: t("map_chats", "Map Chats") },
    { key: "pair-chat", to: "/pair-chat", icon: <MessageCircle size={20} />, label: t("nav.pairChat", "Pair Chat") },
    { key: "uploads", action: "uploads", icon: <Plus size={22} />, label: t("personal_new_uploads", "New uploads") },
    { key: "notifications", action: "bell", icon: <Bell size={20} />, label: t("nav.notifications", "Notifications") },
    { key: "options", action: "options", icon: <Menu size={20} />, label: t("personal_page_options", "Page options") },
  ];
  const isOpen = (a?: NavAction) => (a === "bell" ? notiOpen : a === "uploads" ? uploadsOpen : a === "options" ? optionsOpen : false);
  const act = (a?: NavAction) => {
    if (a === "bell") setNotiOpen(true);
    else if (a === "uploads") setUploadsOpen(true);
    else if (a === "options") setOptionsOpen(true);
  };

  const onPostCreated = () => {
    void qc.invalidateQueries({ queryKey: ["personalPosts"] });
    void qc.invalidateQueries({ queryKey: ["typeCounts"] });
    void qc.invalidateQueries({ queryKey: ["personalPage"] });
    void invalidatePersonalFeeds(qc);
  };

  const dot = (inset: number) => ({
    position: "absolute" as const,
    top: inset,
    right: inset,
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "var(--destructive)",
  });

  /** Unread / live markers on an entry; `inset` is their distance from the corner. */
  const badges = (item: NavItem, inset: number, desktop: boolean) => (
    <>
      {item.action === "bell" && notiBadge && (
        <span aria-label="unread notifications" data-testid="sidenav-unread-badge" style={dot(inset)} />
      )}
      {item.to === "/pair-chat" && pairChatBadge > 0 && (
        <span
          aria-label="unread pair chat messages"
          data-testid={desktop ? "sidenav-chat-badge-pair" : undefined}
          style={dot(inset)}
        />
      )}
      {item.to === "/map-chats" && streetChatBadge > 0 && (
        <span
          aria-label="unread street chat messages"
          data-testid={desktop ? "sidenav-chat-badge-street" : undefined}
          style={dot(inset)}
        />
      )}
      {item.to === "/map-chats" && anyLive && <LiveStreamDot inset={inset} label={liveLabel} />}
    </>
  );

  const avatarStyle = {
    background: "var(--primary-fill)",
  };

  return (
    <>
      {/* Desktop: fixed 56px left sidebar */}
      <nav
        aria-label="Main navigation"
        className="hidden md:flex flex-col items-center py-3 gap-1 shrink-0 border-r border-[var(--border)]"
        style={{ width: 56, background: "var(--bg-base)" }}
      >
        {/* Cooperative: the app icon at the top opens the General Wall, like Home (owner 2026-10-29, Figma 3073:3267) */}
        <NavLink
          to="/cooperative"
          title={t("nav_cooperative", "Cooperative")}
          aria-label={t("nav_cooperative", "Cooperative")}
          className="w-9 h-9 mb-5 rounded-xl flex items-center justify-center shrink-0 transition-colors hover:bg-[var(--bg-elevated)] text-[var(--foreground)]"
        >
          {({ isActive }) => <CooperativeIcon filled={isActive} />}
        </NavLink>

        {navItems.map((item) => (
          <NavEntry
            key={item.key}
            item={item}
            label={item.label}
            open={isOpen(item.action)}
            onAction={() => act(item.action)}
            className={(isActive) =>
              [
                "relative flex items-center justify-center w-10 h-10 rounded-full transition-colors",
                item.key === "options" ? "mt-6" : "",
                isActive
                  ? "bg-[var(--bg-elevated)] text-[var(--foreground)]"
                  : "text-[var(--foreground)] hover:bg-[var(--bg-elevated)]",
              ].join(" ")
            }
          >
            {item.icon}
            {badges(item, 6, true)}
          </NavEntry>
        ))}

        {/* User avatar → own Personal page */}
        <NavLink
          to="/profile/me"
          title={t("nav.profile", "Profile")}
          aria-label={t("nav.profile", "Profile")}
          data-testid="nav-profile"
          className={({ isActive }) =>
            `mt-2 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white shrink-0 transition-opacity hover:opacity-80 ${isActive ? "ring-2 ring-offset-2 ring-offset-[var(--bg-base)] ring-[var(--border-strong)]" : ""}`
          }
          style={avatarStyle}
        >
          {initial}
        </NavLink>
      </nav>

      {/* Mobile: bottom tab bar, icons only; the name is the tooltip and the
          accessible label, as on the desktop sidebar. */}
      <nav
        aria-label="Main navigation"
        className="md:hidden fixed bottom-0 left-0 right-0 flex items-center justify-around border-t border-[var(--border)]"
        style={{ background: "var(--bg-base)", height: 56, zIndex: 2000 }}
      >
        {navItems
          .map((item) => (
            <NavEntry
              key={item.key}
              item={item}
              label={item.label}
              open={isOpen(item.action)}
              onAction={() => act(item.action)}
              className={(isActive) =>
                `relative flex items-center justify-center w-10 h-11 ${isActive ? "text-[var(--primary-light)]" : "text-[var(--foreground-subtle)]"}`
              }
            >
              {item.icon}
              {badges(item, 4, false)}
            </NavEntry>
          ))}
        <NavLink
          to="/profile/me"
          aria-label={t("nav.profile", "Profile")}
          title={t("nav.profile", "Profile")}
          className={({ isActive }) =>
            `flex items-center justify-center w-10 h-11 ${isActive ? "text-[var(--primary-light)]" : "text-[var(--foreground-subtle)]"}`
          }
        >
          <span
            className="w-5 h-5 rounded-full flex items-center justify-center font-bold text-white"
            style={{ ...avatarStyle, fontSize: 10 }}
            data-testid="mobile-avatar"
          >
            {initial}
          </span>
        </NavLink>
      </nav>

      <NotificationsPanel open={notiOpen} onOpenChange={setNotiOpen} />

      {uploadsOpen && (
        <NewUploadsModal
          onClose={() => setUploadsOpen(false)}
          onPersonal={() => {
            setUploadsOpen(false);
            setCreating(true);
          }}
          onNews={() => {
            setUploadsOpen(false);
            setCreatingNews(true);
          }}
          onEvent={() => {
            setUploadsOpen(false);
            setCreatingEvent(true);
          }}
          onBlog={() => {
            setUploadsOpen(false);
            setCreatingBlog(true);
          }}
          onBook={() => {
            setUploadsOpen(false);
            setCreatingBook(true);
          }}
          onApopilo={() => {
            setUploadsOpen(false);
            setCreatingOffer(true);
          }}
        />
      )}
      {optionsOpen && (
        <PageOptionsMenu
          onClose={() => setOptionsOpen(false)}
          onEditProfile={() => setEditOpen(true)}
          onPayments={() => setPaymentsOpen(true)}
        />
      )}
      {editOpen && <EditProfileModal onClose={() => setEditOpen(false)} />}
      {paymentsOpen && <PaymentsModal onClose={() => setPaymentsOpen(false)} />}
      {creatingNews && (
        <PostEditorModal
          target={{ type: "news" }}
          existing={null}
          onClose={() => setCreatingNews(false)}
          onSaved={() => {
            onPostCreated();
            setCreatingNews(false);
          }}
        />
      )}
      {creatingOffer && (
        <OfferComposerModal
          existing={null}
          onClose={() => setCreatingOffer(false)}
          onSaved={() => {
            onPostCreated();
            setCreatingOffer(false);
          }}
        />
      )}
      {creatingBlog && (
        <BlogComposerModal
          existing={null}
          onClose={() => setCreatingBlog(false)}
          onSaved={() => {
            onPostCreated();
            setCreatingBlog(false);
          }}
        />
      )}
      {creatingBook && (
        <BookComposerModal
          existing={null}
          onSwitch={(kind) => {
            setCreatingBook(false);
            if (kind === "personal") setCreating(true);
            else if (kind === "news") setCreatingNews(true);
            else if (kind === "apopilo") setCreatingOffer(true);
            else if (kind === "blog") setCreatingBlog(true);
            else setCreatingEvent(true);
          }}
          onClose={() => setCreatingBook(false)}
          onSaved={() => {
            onPostCreated();
            setCreatingBook(false);
          }}
        />
      )}
      {creatingEvent && (
        <PostEditorModal
          target={{ type: "event" }}
          existing={null}
          onClose={() => setCreatingEvent(false)}
          onSaved={() => {
            onPostCreated();
            setCreatingEvent(false);
          }}
        />
      )}
      {creating && (
        <PostEditorModal
          target={{ type: "personal" }}
          existing={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            onPostCreated();
            setCreating(false);
          }}
        />
      )}
    </>
  );
}
